import { Box, Text, SimpleGrid, Divider, Badge, Button, HStack, useToast, Heading, Textarea, Flex } from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useEffect, useState } from "react";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";
import RouteFlow from "../../components/RouteFlow";
import { usePaymentLinkExpiry } from "../../hooks/usePaymentLinkExpiry";
import { usePaymentStatusPolling } from "../../hooks/usePaymentStatusPolling";
import { Info } from "lucide-react";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const InfoCard = ({ title, children }) => (
  <Box bg="white" borderRadius="lg" boxShadow="sm" p={6}>
    <Text fontWeight="600" mb={3} color="gray.700">
      {title}
    </Text>
    <Divider mb={5} />
    {children}
  </Box>
);

const InfoItem = ({ label, value }) => (
  <Box>
    <Text fontSize="sm" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontWeight="500" color="gray.800">
      {value ?? "-"}
    </Text>
  </Box>
);

const PendingTokenDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [manualPaymentComment, setManualPaymentComment] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  const [paymentLinkSentTime, setPaymentLinkSentTime] = useState(null);
  const [paymentLinkExpiresAt, setPaymentLinkExpiresAt] = useState(null);
  const { isExpired, formattedTime, expiresAt } = usePaymentLinkExpiry(
    paymentLinkSentTime,
    paymentLinkExpiresAt
  );

  const handleSendToken = async () => {
    try {
      // Check if a payment link is already active
      if (paymentLinkSentTime && !isExpired) {
        const expiryTime = paymentLinkExpiresAt
          ? new Date(paymentLinkExpiresAt)
          : expiresAt ||
          new Date(paymentLinkSentTime.getTime() + 16 * 60 * 1000);
        const formattedExpiry = expiryTime.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        toast({
          title: "Payment Link Already Active",
          description: `A payment link is already active. You can resend after it expires at ${formattedExpiry}. Time remaining: ${formattedTime}`,
          status: "warning",
          duration: 2000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem("prima_session");
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Session token not found. Please login again.",
          status: "error",
          duration: 4000,
        });
        return;
      }

      const lead = tokenData?.lead;
      const booking = tokenData?.booking;

      if (!lead?.leadID) {
        toast({
          title: "Error",
          description: "Lead ID is missing.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      if (!tokenAmount || tokenAmount <= 0) {
        toast({
          title: "Invalid Token Amount",
          description: "Token amount must be at least ₹1.",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      // Get customer data from available sources
      const customer = tokenData.customer || lead.customer || booking?.customer || {};
      const userPhone = customer.mobile || "";
      const userEmail = customer.email || "";

      // Validate required fields before sending
      if (!userPhone) {
        toast({
          title: "Missing Phone Number",
          description: "Customer phone number is required to send token request.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      const payload = {
        amount: Number(tokenAmount),
        userPhone: userPhone,
        userEmail: userEmail,
        orderId: lead.leadID,
        leadId: lead._id,
        customerId: customer._id || null,
      };

      setPaymentLoading(true);

      const res = await axios.post(`${SERVER}/payments/send-token`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast({
        title: "Token Request Sent!",
        description: "Payment link has been sent to customer.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Set payment link sent time for expiry tracking
      setPaymentLinkSentTime(new Date());

      // Calculate expiry time (16 minutes from now)
      const expiryTime = new Date(Date.now() + 16 * 60 * 1000);
      setPaymentLinkExpiresAt(expiryTime);
      const formattedExpiry = expiryTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      // toast({
      //   title: "Link Expires Soon",
      //   description: `Payment link will expire at ${formattedExpiry} (16 minutes from now). You can resend after it expires.`,
      //   status: "info",
      //   duration: 5000,   
      //   isClosable: true,
      // });

      // Use the new payment ID from response to fetch updated data
      const newPaymentId = res.data.paymentId;
      if (newPaymentId) {
        const tokenRes = await axios.get(
          `${SERVER}/management/pending-token/${newPaymentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTokenData(tokenRes.data?.data || null);
      }

      // Navigate back to bookings list
      // navigate('/bookings');
    } catch (error) {
      console.error("Token Error:", error?.response?.data || error.message);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Unknown error occurred";
      toast({
        title: "Error Sending Token",
        description: `Request failed: ${errorMessage}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        const tokenRes = await axios.get(
          `${SERVER}/management/pending-token/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // console.log("ID param 👉", id);
        console.log("FULL RESPONSE in pending token details page: ", tokenRes.data);
        setTokenData(tokenRes.data?.data || null);
      } catch (err) {
        console.error("ERROR 👉", err);
        setTokenData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (!tokenData) return;

    const sentAt = tokenData.createdAt ? new Date(tokenData.createdAt) : null;
    const expiryAt = tokenData.expiresAt ? new Date(tokenData.expiresAt) : null;

    if (sentAt) setPaymentLinkSentTime(sentAt);
    if (expiryAt) setPaymentLinkExpiresAt(expiryAt);
  }, [tokenData]);

  useEffect(() => {
    if (!tokenData?.status) return;
    const normalizedStatus = String(tokenData.status).toUpperCase();
    setPaymentStatus(normalizedStatus === "PENDING" ? "PENDING" : "PAID");
  }, [tokenData]);

  // Auto-poll payment status using custom hook
  usePaymentStatusPolling({
    paymentLinkIds: tokenData?.paymentLinkIdField,
    enabled: paymentStatus === "PENDING" && !!tokenData?.paymentLinkIdField,
    onPaymentDetected: async () => {
      setPaymentStatus("PAID");

      toast({
        title: "Payment Received!",
        description: "Token payment has been successfully processed.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh data using the current tokenData._id
      const token = localStorage.getItem("prima_session");
      const paymentIdToFetch = tokenData._id;
      const tokenRes = await axios.get(
        `${SERVER}/management/pending-token/${paymentIdToFetch}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedTokenData = tokenRes.data?.data || null;
      setTokenData(updatedTokenData);

      toast({
        title: "Payment Received",
        description: "Lead has been moved to Bookings.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Navigate to bookings after successful payment detection
      setTimeout(() => {
        navigate("/bookings");
      }, 1500);
    },
  });

  if (loading) {
    return <Loading />;
  }

  if (!tokenData) return <Text>No data found</Text>;

  // Safe fallback
  // const booking = tokenData.booking || {};
  // const customer = booking.customer || {};
  const booking = tokenData.booking;
  const lead = tokenData.lead;
  const customerData =
    tokenData.customer && typeof tokenData.customer === "object"
      ? tokenData.customer
      : tokenData.booking?.customer || {};
  const tripSource = booking || lead || {};
  const tripStops =
    booking?.tripDetails?.stops ||
    lead?.tripDetails?.stops ||
    booking?.leadID?.tripDetails?.stops ||
    [];
  const normalizedTripType = String(
    tripSource?.tripType ||
    tripSource?.type ||
    tripSource?.tripDetails?.type ||
    "",
  ).toUpperCase();
  const isLocalRental = ["LR", "HR", "LOCAL", "LOCALRENTAL", "LOCAL_RENTAL"].includes(
    normalizedTripType,
  );
  const dropLocationValue = (
    tripSource?.toLocation || tripSource?.dropLocation || ""
  )
    .toString()
    .trim()
    .toLowerCase();
  const shouldShowDropLocation = !isLocalRental && dropLocationValue !== "local";
  const bookingStatus = booking?.status ? booking.status : "PENDING";
  const leadId = lead?.leadID || booking?.leadID || tokenData?.lead?.leadID || "-";

  // Extract final price from available data for display
  const finalPrice =
    booking?.fareDetails?.total ||
    lead?.fareDetails?.total ||
    lead?.estimatedFare ||
    0;

  // Token amount should come from booking data (same as shown in UI card)
  const bookingTokenAmount =
    booking?.tokenAmount ||
    booking?.fareDetails?.tokenAmount ||
    booking?.fareDetails?.token_amount ||
    booking?.token_amount;

  const leadTokenAmount =
    lead?.tokenAmount ||
    lead?.fareDetails?.tokenAmount ||
    lead?.fareDetails?.token_amount ||
    lead?.token_amount;

  const tokenAmount = Number(
    bookingTokenAmount ??
    leadTokenAmount ??
    tokenData?.amount ??
    (finalPrice ? Math.round(finalPrice * 0.15) : 0),
  );

  // Debug logging
  // console.log("TokenData:", tokenData);
  // console.log("Booking:", booking);
  // console.log("Lead:", lead);
  // console.log("Booking fareDetails:", booking?.fareDetails);
  // console.log("Lead estimatedFare:", lead?.estimatedFare);

  // --------------------------------------------
  // MARK AS PAID
  // --------------------------------------------
  const handleMarkAsPaid = async () => {
    // console.log("handleMarkAsPaid CALLED!");
    setMarkingPaid(true);

    try {
      const token = localStorage.getItem("prima_session");

      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Session token not found. Please login again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setMarkingPaid(false);
        return;
      }

      const transactionID = `TXN${Date.now()}`;

      if (!id) {
        toast({
          title: "Error",
          description: "Payment ID is missing.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setMarkingPaid(false);
        return;
      }

      const leadId = lead?._id || booking?.lead;
      if (!leadId) {
        toast({
          title: "Error",
          description: "Lead ID is missing.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setMarkingPaid(false);
        return;
      }

      const amount = tokenAmount || 0;
      if (!amount || isNaN(amount) || amount <= 0) {
        toast({
          title: "Invalid Amount",
          description: "Token amount is missing or invalid.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setMarkingPaid(false);
        return;
      }

      let res;
      // If a receipt file is attached, send multipart/form-data
      if (receiptFile || manualPaymentComment) {
        const form = new FormData();
        form.append("leadId", leadId);
        form.append("transactionID", transactionID);
        form.append("amount", Number(amount));
        form.append("manualOverride", true);
        if (receiptFile) form.append("receipt", receiptFile);
        if (manualPaymentComment) form.append("comment", manualPaymentComment);

        res = await axios.patch(
          `${SERVER}/management/payment-paid/${id}`,
          form,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        res = await axios.patch(
          `${SERVER}/management/payment-paid/${id}`,
          {
            leadId,
            transactionID,
            amount: Number(amount),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast({
        title: "Payment Marked Paid",
        description: "Payment successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Update payment status - this will automatically stop polling via the custom hook
      setPaymentStatus("PAID");

      // Clear receipt after success
      setReceiptFile(null);
      setReceiptPreview(null);
      setManualPaymentComment("");

      // Backend already converts lead to booking when marking payment as paid
      toast({
        title: "Success!",
        description: "Payment marked as paid and lead converted to booking.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Navigate back to bookings list after a short delay
      setTimeout(() => {
        navigate("/bookings");
      }, 1500);

    } catch (error) {
      console.error("Payment Error:", error);

      let errorMessage = "An error occurred while marking payment as paid";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error Marking Payment",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setMarkingPaid(false);
    }
  };

  const statusColor = paymentStatus === "PAID" ? "green" : "orange";

  return (
    <Box bg="#f5f7fb" minH="100vh" p={6}>
      <BreadCrumbMaker
        items={[
          { title: "Bookings", link: "/bookings" },
          {
            title: "View Pending Token",
            link: `/booking/pending-token/${id}`,
            on: true,
          },
        ]}
      />

      <InfoCard title="Lead Information">
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          {/* <InfoItem label="Lead ID" value={booking.leadID || "-"} /> */}
          <InfoItem
            label="Lead ID"
            value={lead?.leadID || booking?.leadID || "-"}
          />
          <InfoItem label="Name" value={customerData.name || "-"} />
          <InfoItem label="Contact number" value={customerData.mobile || "-"} />
          <InfoItem
            label="Date"
            value={
              tripSource.pickupDateTime || tripSource.dateTime
                ? new Date(
                  tripSource.pickupDateTime || tripSource.dateTime,
                ).toLocaleString()
                : "-"
            }
          />

          <InfoItem
            label="Token Amount"
            value={tokenAmount ? `₹ ${tokenAmount}` : "-"}
          />

          <InfoItem
            label="Estimated Fare"
            value={
              booking?.fareDetails?.baseFare
                ? `₹ ${booking.fareDetails.baseFare}`
                : lead?.fareDetails?.baseFare
                  ? `₹ ${lead.fareDetails.baseFare}`
                  : lead?.estimatedFare
                    ? `₹ ${lead.estimatedFare}`
                    : "-"
            }
          />

          <InfoItem
            label="Discount"
            value={
              booking?.fareDetails?.discounts
                ? `₹ ${booking.fareDetails.discounts}`
                : lead?.fareDetails?.discounts
                  ? `₹ ${lead.fareDetails.discounts}`
                  : "₹ 0"
            }
          />

          <InfoItem
            label="Final Price"
            value={
              booking?.fareDetails?.total
                ? `₹ ${booking.fareDetails.total}`
                : lead?.fareDetails?.total
                  ? `₹ ${lead.fareDetails.total}`
                  : lead?.estimatedFare
                    ? `₹ ${lead.estimatedFare}`
                    : "-"
            }
          />

          <InfoItem
            label="Pickup Location"
            value={tripSource.fromLocation || tripSource.pickupLocation || "-"}
          />
          <InfoItem
            label="Specific Pickup Location"
            value={tripSource.pickupLocation || "-"}
          />
          {shouldShowDropLocation && (
            <InfoItem
              label="Drop Location"
              value={tripSource.toLocation || tripSource.dropLocation || "-"}
            />
          )}
          {shouldShowDropLocation && (
            <InfoItem
              label="Specific Drop Location"
              value={tripSource.dropLocation || "-"}
            />
          )}

          <InfoItem
            label="Car Model"
            value={tripSource.vehicleSegment?.segmentName || "-"}
          />

          <Box>
            <Text fontSize="sm" color="gray.500" mb={1}>
              Booking Status
            </Text>
            <Badge colorScheme="yellow" variant="subtle">
              {bookingStatus}
            </Badge>
          </Box>
          {/* <InfoItem
            label="Date"
            value={
              booking.dateTime
                ? new Date(booking.dateTime).toLocaleString()
                : "-"
            }
          />
          <InfoItem
            label="Price"
            value={
              booking.fareDetails?.total
                ? `₹ ${booking.fareDetails.total}`
                : "-"
            }
          />
          <InfoItem label="Car Model" value={booking.vehicleSegment || "-"} />
          <InfoItem label="Pickup Location" value={booking.pickupLocation || "-"} />
          <InfoItem label="Drop Location" value={booking.dropLocation || "-"} /> */}
        </SimpleGrid>

        {Array.isArray(tripStops) && tripStops.length > 0 && (
          <Box mt={4}>
            <RouteFlow
              from={tripSource.fromLocation || tripSource.pickupLocation}
              to={tripSource.toLocation || tripSource.dropLocation}
              stops={tripStops}
              tripType={tripSource.type}
            />
          </Box>
        )}
      </InfoCard>

      {/* <Box mt={6}>
        <InfoCard title="Status Information">
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Booking Status
              </Text>
              <Badge colorScheme="yellow" variant="subtle">
                {bookingStatus}
              </Badge>
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Token Payment Status
              </Text>
              <Badge
                colorScheme={
                  tokenData.status === "Pending" ? "orange" : "green"
                }
                variant="subtle"
              >
                {tokenData.status || "-"}
              </Badge>
            </Box>

            <InfoItem label="Token ID" value={tokenData.token || "-"} />
            <InfoItem label="Payment ID" value={tokenData.paymentID || "-"} />
          </SimpleGrid>

          <Box mt={5}>
            <Text fontSize="sm" color="gray.500" mb={1}>
              Notes
            </Text>
            <Text fontSize="sm" color="gray.700">
              {booking?.notes || "-"}
            </Text>
          </Box>
        </InfoCard>
      </Box> */}

      {/* Payment Actions - Only show if status is Pending */}
      {tokenData.status === "Pending" && (
        <Box mt={6} p={4} bg="blue.50" borderRadius="md">
          <Heading size="sm" mb={2}>
            Token Payment
          </Heading>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Send a token payment request to the customer (Token amount:
            ₹{tokenAmount || 0})
          </Text>
          <Flex gap={4} align="center">
            <Button
              colorScheme="blue"
              size="sm"
              onClick={handleSendToken}
              isLoading={paymentLoading}
              isDisabled={!tokenAmount || tokenAmount <= 0 || (paymentLinkSentTime && !isExpired)}
              style={{
                opacity:
                  paymentLinkSentTime && !isExpired ? 0.6 : 1,
              }}
            >
              Send Token Request
            </Button>
            {!isExpired && paymentLinkSentTime && (
              <Text fontSize="sm" fontWeight="600" color="orange.600">
                Payment Link already sent, Expires in: {formattedTime}
              </Text>
            )}
          </Flex>
          { }
        </Box>
      )}

      {/* Token Payment Status */}
      <Box
        bg="white"
        p={6}
        rounded="md"
        border="1px solid #E8EDF7"
        boxShadow="sm"
      >
        <Heading size="md" mb={5} color="#2C3653">
          Token Payment Status
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box>
            <Text fontSize="sm" color="#6B7280">
              Status
            </Text>
            <Badge
              mt={1}
              px={3}
              py={1}
              colorScheme={statusColor}
              rounded="md"
            >
              {paymentStatus}
            </Badge>
          </Box>
          <Box>
            <Text fontSize="sm" color="#6B7280">
              Token Reference
            </Text>
            <Text fontWeight="600">{`TOKEN-${leadId?.split('-').pop() || ''}`}</Text>
          </Box>
        </SimpleGrid>

        <Text mt={4} color="#555" fontSize="sm">
          {paymentStatus === "PENDING"
            ? "Waiting for customer to complete the payment using the token."
            : "Payment has been successfully completed."}
        </Text>

        {/* Manual Payment Section */}
        <Box mt={4}>
          <Text fontWeight="600" mb={2} fontSize="sm">
            Manual Payment Confirmation
          </Text>

          {/* Receipt Upload */}
          <Box mb={3}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Upload Payment Receipt (Optional)
            </Text>
            <input
              id="receiptFile"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files[0];
                if (f) {
                  setReceiptFile(f);
                  setReceiptPreview(URL.createObjectURL(f));
                } else {
                  setReceiptFile(null);
                  setReceiptPreview(null);
                }
              }}
            />
            <label htmlFor="receiptFile">
              <Button size="sm" as="span" variant="outline">
                {receiptFile ? "Change Receipt" : "Upload Receipt"}
              </Button>
            </label>
            {receiptPreview && (
              <Box mt={2}>
                <img
                  src={receiptPreview}
                  alt="receipt"
                  style={{
                    maxWidth: 240,
                    maxHeight: 160,
                    borderRadius: "4px",
                  }}
                />
                <Button
                  size="xs"
                  mt={1}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => {
                    setReceiptFile(null);
                    setReceiptPreview(null);
                  }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>

          {/* Payment Comment */}
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Payment Notes/Comments
            </Text>
            <Textarea
              placeholder="Enter payment details, transaction ID, or any notes..."
              value={manualPaymentComment}
              onChange={(e) => setManualPaymentComment(e.target.value)}
              size="sm"
              resize="vertical"
              minH="80px"
            />
          </Box>
        </Box>

        <Button
          mt={5}
          colorScheme="blue"
          w="140px"
          onClick={handleMarkAsPaid}
          isLoading={markingPaid}
          isDisabled={paymentStatus === "PAID"}
        >
          Mark as Paid
        </Button>
      </Box>

    </Box>
  );
};

export default PendingTokenDetails;
